import { createApiClient } from '@rconf/api-client';
import { createAuthSession, createBrowserAuthAdapter } from '@rconf/auth-session';

const DEFAULT_HANDOFF = '/conferencing/meetings/';
const RBOT_REQUEST_PATH = '/api/auth/rbot/request';
const RBOT_VERIFY_PATH = '/api/auth/rbot/verify';

export function createAccountAuthRuntime(options = {}) {
  const fetchImpl = options.fetchImpl || globalThis.fetch;
  const adapter = createBrowserAuthAdapter({
    baseUrl: options.baseUrl || '',
    fetchImpl,
    credentials: 'include',
  });
  const session = createAuthSession({ adapter });
  const client = createApiClient({
    baseUrl: options.baseUrl || '',
    fetchImpl,
    credentials: 'include',
    ...session.apiClientOptions(),
  });
  const locationLike = options.location || globalThis.location;
  const navigate = options.navigate || ((url) => locationLike.replace(url));
  let bootstrapPromise = null;

  function restore() {
    if (!bootstrapPromise) {
      bootstrapPromise = session.restore().then((state) => {
        if (state?.error) bootstrapPromise = null;
        return state;
      });
    }
    return bootstrapPromise;
  }

  return Object.freeze({
    getState: session.getState,
    subscribe: session.subscribe,
    request: client.request,
    restore,
    retryRestore() {
      bootstrapPromise = null;
      return restore();
    },
    loginWithPassword(credentials) {
      return session.login(credentials);
    },
    requestRendallCode(input) {
      return client.post(RBOT_REQUEST_PATH, input, { auth: false, retryAuth: false });
    },
    verifyRendallCode(input) {
      return session.login(input, { path: RBOT_VERIFY_PATH });
    },
    logout() {
      bootstrapPromise = null;
      return session.logout();
    },
    impersonate(userId) {
      return client.post(`/api/admin/users/${encodeURIComponent(String(userId || '').trim())}/impersonate`)
        .then(() => {
          session.clear();
          bootstrapPromise = null;
          return restore();
        });
    },
    stopImpersonation() {
      return client.post('/api/auth/impersonation/stop').then(() => {
        session.clear();
        bootstrapPromise = null;
        return restore();
      });
    },
    handoff() {
      const url = resolveAccountHandoff(options.handoffUrl, locationLike);
      navigate(url);
      return url;
    },
  });
}

export function resolveAccountHandoff(value, locationLike = globalThis.location) {
  const origin = safeOrigin(locationLike);
  try {
    const target = new URL(String(value || DEFAULT_HANDOFF), `${origin}/`);
    if (target.origin !== origin) return DEFAULT_HANDOFF;
    if (!target.pathname.startsWith('/')) return DEFAULT_HANDOFF;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return DEFAULT_HANDOFF;
  }
}

export function initialAuthMethod(search = '') {
  const method = new URLSearchParams(String(search || '')).get('auth');
  return method === 'rbot' || method === 'rendall' ? 'rendall' : 'password';
}

function safeOrigin(locationLike) {
  const value = String(locationLike?.origin || '').trim();
  if (/^https?:\/\//i.test(value)) return value.replace(/\/+$/, '');
  return 'https://account.rconf.local';
}
